import type { LoomAcceptedMeshHeader } from "./loomAcceptedMeshContract";
import type {
  LoomAcceptedUnionReceipt,
  LoomUnionSharedSeamReceipt,
} from "./loomAcceptedUnionReceipt";

interface ArrayReaders {
  f32: (name: string) => Float32Array;
  u32: (name: string) => Uint32Array;
}

export interface LoomDecodedUnionSharedSeam extends LoomUnionSharedSeamReceipt {
  vertexIndices: Uint32Array;
  parametersA: Float32Array;
  parametersB: Float32Array;
}

export interface LoomDecodedUnionReceipt {
  header: LoomAcceptedUnionReceipt;
  faceSourceNodeIndices: Uint32Array;
  facePatchIndices: Uint32Array;
  faceCornerParameters: Float32Array;
  sharedSeams: LoomDecodedUnionSharedSeam[];
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function incidence(faces: Uint32Array, vertexCount: number): Map<string, number[]> {
  const result = new Map<string, number[]>();
  for (let face = 0; face < faces.length / 3; face += 1) {
    const row: [number, number, number] = [
      faces[3 * face]!, faces[3 * face + 1]!, faces[3 * face + 2]!,
    ];
    if (new Set(row).size !== 3 || row.some((item) => item >= vertexCount)) {
      throw new Error("v3 union exact surface contains an invalid face");
    }
    for (const [a, b] of [[row[0], row[1]], [row[1], row[2]], [row[2], row[0]]] as const) {
      const key = edgeKey(a, b); result.set(key, [...(result.get(key) ?? []), face]);
    }
  }
  return result;
}

function samePair(left: readonly number[], right: readonly number[]): boolean {
  return left.length === 2 && right.length === 2
    && left.every((item) => right.includes(item));
}

function decodeSeam(
  seam: LoomUnionSharedSeamReceipt, readers: ArrayReaders,
  edges: Map<string, number[]>, exactFaces: Uint32Array,
  faceSources: Uint32Array, facePatches: Uint32Array, vertexCount: number,
): LoomDecodedUnionSharedSeam {
  const vertexIndices = readers.u32(seam.vertexIndicesArray);
  const parametersA = readers.f32(seam.parametersAArray);
  const parametersB = readers.f32(seam.parametersBArray);
  if (vertexIndices.length < 3 || vertexIndices.some((item) => item >= vertexCount)
      || new Set(vertexIndices).size !== vertexIndices.length
      || parametersA.length !== vertexIndices.length * 2
      || parametersB.length !== vertexIndices.length * 2
      || !parametersA.every(Number.isFinite) || !parametersB.every(Number.isFinite)) {
    throw new Error(`v3 union seam ${seam.seamId} arrays are malformed`);
  }
  for (let index = 0; index < vertexIndices.length; index += 1) {
    const first = vertexIndices[index]!;
    const second = vertexIndices[(index + 1) % vertexIndices.length]!;
    const owners = edges.get(edgeKey(first, second)) ?? [];
    if (owners.length !== 2
        || !samePair(owners.map((face) => faceSources[face]!), seam.sourceNodeIndices)
        || !samePair(owners.map((face) => facePatches[face]!), seam.patchIndices)) {
      throw new Error(`v3 union seam ${seam.seamId} is not a synchronized two-source edge chain`);
    }
    for (const face of owners) {
      const row = [exactFaces[3 * face]!, exactFaces[3 * face + 1]!, exactFaces[3 * face + 2]!];
      if (!row.includes(first) || !row.includes(second)) {
        throw new Error(`v3 union seam ${seam.seamId} face incidence is stale`);
      }
    }
  }
  return { ...seam, vertexIndices, parametersA, parametersB };
}

export function decodeLoomAcceptedUnionReceipt(
  accepted: LoomAcceptedMeshHeader, exactFaces: Uint32Array,
  exactVertexCount: number, readers: ArrayReaders,
): LoomDecodedUnionReceipt | undefined {
  const header = accepted.unionReceipt;
  if (!header) return undefined;
  const faceSourceNodeIndices = readers.u32(header.faceSourceNodeIndicesArray);
  const facePatchIndices = readers.u32(header.facePatchIndicesArray);
  const faceCornerParameters = readers.f32(header.faceCornerParametersArray);
  const faceCount = exactFaces.length / 3;
  if (faceSourceNodeIndices.length !== faceCount || facePatchIndices.length !== faceCount
      || faceCornerParameters.length !== faceCount * 3 * 2
      || !faceCornerParameters.every(Number.isFinite)) {
    throw new Error("v3 union exact face evidence has an invalid extent or value");
  }
  for (let face = 0; face < faceCount; face += 1) {
    const source = faceSourceNodeIndices[face]!; const patch = facePatchIndices[face]!;
    if (source >= accepted.sourceNodes.length || patch >= accepted.patches.length
        || accepted.patches[patch]!.sourceNodeIndex !== source
        || accepted.patches[patch]!.evaluatorKind !== "exact-parametric") {
      throw new Error("v3 union exact face ownership is stale or out of range");
    }
  }
  const edgeOwners = incidence(exactFaces, exactVertexCount);
  const sharedSeams = header.sharedSeams.map((seam) => decodeSeam(
    seam, readers, edgeOwners, exactFaces, faceSourceNodeIndices,
    facePatchIndices, exactVertexCount,
  ));
  const seamVertices = sharedSeams.flatMap((seam) => Array.from(seam.vertexIndices));
  if (new Set(seamVertices).size !== seamVertices.length) {
    throw new Error("v3 union shared seam loops overlap in exact vertex space");
  }
  for (const pair of header.pairReceipts) {
    const loops = sharedSeams.filter((seam) => samePair(
      seam.sourceNodeIndices, pair.sourceNodeIndices,
    ));
    if (loops.length !== pair.loopCount || pair.patchPairCount < 1
        || pair.seedEdgeCount < loops.length) {
      throw new Error("v3 union pair receipt does not bind its shared seams");
    }
  }
  return {
    header, faceSourceNodeIndices, facePatchIndices,
    faceCornerParameters, sharedSeams,
  };
}
